import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import WalletVault from './pages/WalletVault';
import ServerSeeds from './pages/ServerSeeds';
import BackendManager from './pages/BackendManager';
import Login from './pages/Login';
import { logout, checkAuthStatus } from './services/api';
import './index.css';

// Protected Route Component
function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      try {
        await checkAuthStatus();
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_email');
        localStorage.removeItem('token_expires_in');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Main App Content (with tabs)
function AppContent() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const adminEmail = localStorage.getItem('admin_email');

  return (
    <div className="App bg-black min-h-screen">
      {/* Navigation Tabs */}
      <div className="bg-zinc-900 shadow-md border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === 'dashboard'
                    ? 'border-orange-500 text-orange-400'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-zinc-600'
                }`}
              >
                📊 Dashboard
              </button>
              <button
                onClick={() => setActiveTab('vault')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === 'vault'
                    ? 'border-orange-500 text-orange-400'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-zinc-600'
                }`}
              >
                💼 Wallet Vault
              </button>
              <button
                onClick={() => setActiveTab('seeds')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === 'seeds'
                    ? 'border-orange-500 text-orange-400'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-zinc-600'
                }`}
              >
                🔐 Server Seeds
              </button>
              <button
                onClick={() => setActiveTab('backend')}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === 'backend'
                    ? 'border-orange-500 text-orange-400'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-zinc-600'
                }`}
              >
                ⚙️ Backend Manager
              </button>
            </div>
            <div className="flex items-center gap-4">
              {adminEmail && (
                <span className="text-sm text-gray-400">Logged in as: {adminEmail}</span>
              )}
              <button
                onClick={handleLogout}
                className="text-sm text-orange-400 hover:text-orange-300 font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'dashboard' && <Dashboard />}
      {activeTab === 'vault' && <WalletVault />}
      {activeTab === 'seeds' && <ServerSeeds />}
      {activeTab === 'backend' && <BackendManager />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppContent />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
